import { Badge } from '@avanta/shared/components/ui/Badge';
import { Button } from '@avanta/shared/components/ui/Button';
import { Checkbox } from '@avanta/shared/components/ui/Checkbox';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { cn } from '@avanta/shared/utils/cn';
import { useShallow } from 'zustand/react/shallow';
import type { ChannelProps } from './types';

export const Channel = ({ channelId, title, unreadMessages }: ChannelProps): React.ReactElement => {
  const { isSelectionMode, isSelected, isActive, setActiveChannel, toggleChannelSelection } =
    useConversationsStore(
      useShallow(state => ({
        isSelectionMode: state.isSelectionMode,
        isSelected: state.selectedChannelIds.has(channelId),
        isActive: state.activeChannelId === channelId,
        setActiveChannel: state.setActiveChannel,
        toggleChannelSelection: state.toggleChannelSelection,
      }))
    );

  const handleClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (isSelectionMode) {
      toggleChannelSelection(channelId);
      return;
    }
    setActiveChannel(channelId);
  };

  return (
    <Button
      variant="ghost"
      className={cn(
        'flex py-6 justify-between items-center rounded-md',
        isSelectionMode ? 'px-1 hover:bg-mint-300' : 'px-3 hover:bg-gray-200',
        isActive && 'bg-gray-200',
        isSelected && 'bg-mint-200'
      )}
      onClick={handleClick}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {isSelectionMode && (
          <Checkbox
            className="shrink-0"
            checked={isSelected}
            onCheckedChange={() => toggleChannelSelection(channelId)}
          />
        )}
        <p className="min-w-0 flex-1 truncate text-sm text-left font-normal">{title}</p>
      </div>
      {!!unreadMessages && (
        <Badge size="sm" className="shrink-0 bg-teal-600 text-white rounded-full h-6">
          {unreadMessages}
        </Badge>
      )}
    </Button>
  );
};
